import React, { useState } from 'react';
import { observer } from 'src/utils/mobx-react';
import { action, computed, observable, makeAutoObservable } from 'mobx';
import { useAppStateContext } from 'src/AppState/AppState';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { FooterDataModelType } from 'src/api/config/footerManagement/footerLegalText/getFooterText';
import { FooterContactUsDataModelType } from 'src/api/config/footerManagement/footerContactUsElements/getContactUsElements';
import { FooterLegalTextForm } from './FooterLegalText/FooterLegalTextForm';
import { FooterContactUsElementsForm } from './FooterContactUs/FooterContactUsElementsForm';
import { MainContent } from 'src/domains/layouts/shared/layout/Layout.style';
import { TabsWrapper, TabItem } from 'src/domains/layouts/shared/tabs/Tabs.style';
import { Loader } from 'src/domains/layouts/shared/loader/Loader';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { z } from 'zod';

export class FooterLegalTextState {
    @observable private readonly footerLegalTextDataResource: Resource<FooterDataModelType | null>;

    public constructor(private readonly trpcClient: TrpcClient) {
        this.footerLegalTextDataResource = new Resource(async (): Promise<FooterDataModelType | null> => {
            const response = await this.trpcClient.client.footerTextRouter.getFooterText.query();
            if (response.status === 200) {
                return response.body;
            }
            return null;
        });
        makeAutoObservable(this);
    }

    @computed public get isLegalTextDataLoading(): boolean {
        const footerDetailsData = this.footerLegalTextDataResource.get();
        return footerDetailsData.type === 'loading';
    }

    public refreshLegalTextData = async (): Promise<void> => {
        await this.footerLegalTextDataResource.refresh();
    };

    @computed public get footerLegalTextDetailsData(): FooterDataModelType | null {
        const footerDetailsData = this.footerLegalTextDataResource.get();
        if (footerDetailsData.type === 'ready') {
            return footerDetailsData.value;
        }
        return null;
    }
}

export const ContactOptionTypeZod = z.enum(['e-mail', 'phone-number', 'twitter', 'facebook', 'instagram']);
export type ContactOptionType = z.infer<typeof ContactOptionTypeZod>;

export class FooterContactUsElementsState {
    @observable public readonly contactUsElementsResource: Resource<Array<FooterContactUsDataModelType> | null>;
    @observable public errors: Array<string> = [];
    public contactOptionsLimit: Record<ContactOptionType, number> = {
        'e-mail': 1,
        'phone-number': 3,
        twitter: 1,
        facebook: 1,
        instagram: 1,
    };

    public constructor(private readonly trpcClient: TrpcClient) {
        this.contactUsElementsResource = new Resource(async (): Promise<Array<FooterContactUsDataModelType> | null> => {
            const response = await this.trpcClient.client.footerContactElementsRouter.getContactElements.query();
            if (response.status === 200) {
                return response.body;
            }
            return null;
        });
        makeAutoObservable(this);
    }

    @computed public get isContactUsDataLoading(): boolean {
        const footerDetailsData = this.contactUsElementsResource.get();
        return footerDetailsData.type === 'loading';
    }

    @computed public get contactUsElementsData(): Array<FooterContactUsDataModelType> {
        const contactUsElementsData = this.contactUsElementsResource.get();
        if (contactUsElementsData.type === 'ready' && contactUsElementsData.value !== null) {
            return contactUsElementsData.value.sort((a, b) => (a.display_order > b.display_order ? 1 : -1));
        }
        return [];
    }

    @computed public get availableContactOptions(): Array<ContactOptionType> {
        const contactOptionsCount = this.contactUsElementsData.reduce(
            (contactOptionsCount, contactUsElement) => {
                try {
                    const elementType = ContactOptionTypeZod.parse(contactUsElement.contact_type);
                    contactOptionsCount[elementType]++;
                    return contactOptionsCount;
                } catch {
                    return contactOptionsCount;
                }
            },
            {
                'e-mail': 0,
                'phone-number': 0,
                twitter: 0,
                facebook: 0,
                instagram: 0,
            }
        );
        return Object.entries(contactOptionsCount).reduce<Array<ContactOptionType>>(
            (availableContactOptions, [contactType, count]) => {
                try {
                    const elementType = ContactOptionTypeZod.parse(contactType);
                    if (count < this.contactOptionsLimit[elementType]) {
                        availableContactOptions.push(elementType);
                    }
                    return availableContactOptions;
                } catch {
                    return availableContactOptions;
                }
            },
            []
        );
    }
}

const ManagementTabZod = z.enum(['legal-text', 'contact-us-elements']);
type ManagementTabType = z.infer<typeof ManagementTabZod>;

class FooterManagementState {
    @observable public managementTab: ManagementTabType;
    private managementUrlParamName = 'management';

    public constructor() {
        const url = new URL(window.location.href);
        try {
            this.managementTab = ManagementTabZod.parse(url.searchParams.get(this.managementUrlParamName));
        } catch {
            this.managementTab = 'legal-text';
        }
        makeAutoObservable(this);
    }

    @action public handleManagementTabChange = (managementTab: ManagementTabType): void => {
        const url = new URL(window.location.href);
        url.searchParams.set(this.managementUrlParamName, managementTab);
        window.history.replaceState({}, '', url);
        this.managementTab = managementTab;
    };
}

export const FooterManagement = observer('FooterManagement', () => {
    const { configState, trpcClient } = useAppStateContext();
    const [footerManagementState] = useState(() => new FooterManagementState());
    const [footerLegalTextState] = useState(() => new FooterLegalTextState(trpcClient));
    const [footerContactElementsState] = useState(() => new FooterContactUsElementsState(trpcClient));

    return (
        <MainContent>
            {configState.config.footerLegalTextTab === false ? null : (
                <TabsWrapper>
                    <TabItem
                        isActive={footerManagementState.managementTab === 'legal-text'}
                        onClick={(): void => footerManagementState.handleManagementTabChange('legal-text')}
                    >
                        Legal text
                    </TabItem>

                    <TabItem
                        isActive={footerManagementState.managementTab === 'contact-us-elements'}
                        onClick={(): void => footerManagementState.handleManagementTabChange('contact-us-elements')}
                        data-test='footer-contact-us-tab-button'
                    >
                        Contact us methods
                    </TabItem>
                </TabsWrapper>
            )}

            {configState.config.footerLegalTextTab === true && footerManagementState.managementTab === 'legal-text' ? (
                <>
                    <Loader isActive={footerLegalTextState.isLegalTextDataLoading} />
                    {footerLegalTextState.isLegalTextDataLoading ? null : (
                        <FooterLegalTextForm footerLegalTextState={footerLegalTextState} />
                    )}
                </>
            ) : (
                <>
                    <Loader isActive={footerContactElementsState.isContactUsDataLoading} />
                    <FooterContactUsElementsForm contactUsState={footerContactElementsState} />
                </>
            )}
        </MainContent>
    );
});
